import { z } from "zod";

export const webSocketTransportStatuses = [
  "idle",
  "listening",
  "thinking",
  "searching",
  "speaking",
  "error",
  "voice_connected",
  "voice_disconnected",
  "voice_error",
] as const;
export type WebSocketTransportStatus =
  (typeof webSocketTransportStatuses)[number];

export const streamedCitationSchema = z.object({
  id: z.string(),
  title: z.string(),
  excerpt: z.string(),
  source: z.string(),
  score: z.number(),
  source_type: z.string().optional().nullable(),
  metadata: z.record(z.string(), z.unknown()).optional().nullable(),
});

export type StreamedCitation = z.infer<typeof streamedCitationSchema>;

export const streamedTextDeltaSchema = z.object({
  type: z.literal("text"),
  text: z.string(),
  is_pii: z.boolean().optional().nullable(),
  pii_type: z.string().optional().nullable(),
});

export const streamedStatusDeltaSchema = z.object({
  type: z.literal("status"),
  status: z.enum(webSocketTransportStatuses),
  details: z.string().optional().nullable(),
  timestamp: z.number().optional().nullable(),
});

export const streamedCitationsDeltaSchema = z.object({
  type: z.literal("citations"),
  citations: z.array(streamedCitationSchema),
});

export const streamedErrorDeltaSchema = z.object({
  type: z.literal("error"),
  code: z.string(),
  message: z.string(),
});

export const streamedAudioDeltaSchema = z.object({
  type: z.literal("audio"),
  audio_data: z.string().optional().nullable(),
  alignment_chars: z.array(z.string()).optional().nullable(),
  alignment_char_start_times_ms: z.array(z.number()).optional().nullable(),
  alignment_char_durations_ms: z.array(z.number()).optional().nullable(),
});

export const streamedTranscriptDeltaSchema = z.object({
  type: z.literal("transcript"),
  text: z.string(),
});

/**
 * Assistant turn: generated or retrieved files. `upload_id` is the persisted
 * message attachment the bytes are downloaded through; `document_id` names the
 * context document behind it and is all older servers send.
 */
export const streamedFileDeltaSchema = z
  .object({
    type: z.literal("file"),
    upload_id: z.string().optional(),
    document_id: z.string(),
    mime: z.string(),
    name: z.string(),
  })
  .passthrough();

export type StreamedFileDelta = z.infer<typeof streamedFileDeltaSchema>;

/**
 * Items in `response.delta.content` that the backend may add before the client
 * is updated (e.g. `speech.chunk`). Parsed and ignored until explicitly handled.
 */
export const streamedPassthroughDeltaSchema = z
  .object({ type: z.string() })
  .passthrough();

export const webSocketDeltaContentSchema = z.union([
  streamedTextDeltaSchema,
  streamedStatusDeltaSchema,
  streamedCitationsDeltaSchema,
  streamedErrorDeltaSchema,
  streamedAudioDeltaSchema,
  streamedTranscriptDeltaSchema,
  streamedFileDeltaSchema,
  streamedPassthroughDeltaSchema,
]);

/** Parsed delta item (includes unknown `type` values from the server). */
export type WebSocketDeltaContent = z.infer<typeof webSocketDeltaContentSchema>;

/** Subset of {@link WebSocketDeltaContent} that the client handles in reducers / chat UI. */
export type WebSocketDeltaContentRecognized =
  | z.infer<typeof streamedTextDeltaSchema>
  | z.infer<typeof streamedStatusDeltaSchema>
  | z.infer<typeof streamedCitationsDeltaSchema>
  | z.infer<typeof streamedErrorDeltaSchema>
  | z.infer<typeof streamedAudioDeltaSchema>
  | z.infer<typeof streamedTranscriptDeltaSchema>
  | StreamedFileDelta;

export function isRecognizedDeltaContentItem(
  item: WebSocketDeltaContent,
): item is WebSocketDeltaContentRecognized {
  switch (item.type) {
    case "audio":
    case "citations":
    case "error":
    case "file":
    case "status":
    case "text":
    case "transcript":
      return true;
    default:
      return false;
  }
}

export type StreamedAudioDelta = z.infer<typeof streamedAudioDeltaSchema>;
export type StreamedStatusDelta = z.infer<typeof streamedStatusDeltaSchema>;

export function isAudioDeltaItem(
  item: WebSocketDeltaContent,
): item is StreamedAudioDelta {
  return isRecognizedDeltaContentItem(item) && item.type === "audio";
}

export function isStatusDeltaItem(
  item: WebSocketDeltaContent,
): item is StreamedStatusDelta {
  return isRecognizedDeltaContentItem(item) && item.type === "status";
}

export const webSocketResponseStartSchema = z
  .object({
    type: z.literal("response.start"),
    thread_id: z.string(),
    session_id: z.string(),
    message_id: z.string().optional().nullable(),
  })
  .passthrough();

export const webSocketResponseDeltaSchema = z
  .object({
    type: z.literal("response.delta"),
    thread_id: z.string(),
    session_id: z.string(),
    /** Backend may omit when sending empty or keep-alive envelopes. */
    content: z.array(webSocketDeltaContentSchema).default([]),
  })
  .passthrough();

export const webSocketDoneStatisticSchema = z.object({
  type: z.literal("statistic"),
  mode: z.string().optional().nullable(),
  completion_tokens: z.number().optional().nullable(),
  prompt_tokens: z.number().optional().nullable(),
  total_tokens: z.number().optional().nullable(),
  cached_tokens: z.number().optional().nullable(),
  /**
   * Hidden-reasoning tokens, a subset of `completion_tokens`. Always sent by
   * the backend (0 when the model did not reason); optional here for older
   * backends.
   */
  reasoning_tokens: z.number().optional().nullable(),
  /**
   * The thread's CUMULATIVE tokens after this turn — not this turn's cost, which
   * is `total_tokens`. Read back from the database post-increment, so a client
   * that missed frames or reloaded mid-conversation self-corrects instead of
   * drifting. Drives the per-conversation usage ring.
   *
   * Absent or 0 means untracked (no per-thread ceiling in force).
   */
  thread_tokens_used: z.number().optional().nullable(),
  /** The per-thread ceiling in force. Absent or negative means unlimited. */
  thread_token_limit: z.number().optional().nullable(),
  thread_title: z.string().optional().nullable(),
  message_id: z.string().optional().nullable(),
  chunk: z.string().optional().nullable(),
  /**
   * Set when the caller barged in and the reply was cut short. `chunk` is
   * then the prefix the caller actually HEARD (shorter than what streamed);
   * the numbers say how much of the generated text that was.
   */
  interrupted: z.boolean().optional().nullable(),
  heard_ms: z.number().optional().nullable(),
  heard_chars: z.number().optional().nullable(),
  generated_chars: z.number().optional().nullable(),
  heard_estimated: z.boolean().optional().nullable(),
  heard_uncertain: z.boolean().optional().nullable(),
});

export const webSocketResponseDoneSchema = z
  .object({
    type: z.literal("response.done"),
    thread_id: z.string(),
    session_id: z.string(),
    message_id: z.string(),
    content: z.array(webSocketDoneStatisticSchema),
  })
  .passthrough();

export const webSocketMessageUpdatedSchema = z.object({
  type: z.literal("response.message_updated"),
  thread_id: z.string().min(1),
  session_id: z.string().min(1),
  message_id: z.string().min(1),
  content: z.string(),
  metadata: z.object({
    interrupted: z.literal(true),
    heard_ms: z.number().nonnegative(),
    heard_chars: z.number().nonnegative(),
    generated_chars: z.number().nonnegative(),
    heard_estimated: z.boolean(),
    heard_uncertain: z.boolean().optional(),
    heard_report: z.string().optional(),
  }),
});
export type WebSocketMessageUpdated = z.infer<
  typeof webSocketMessageUpdatedSchema
>;

export const webSocketResponseStatusSchema = z
  .object({
    type: z.literal("response.status"),
    thread_id: z.string(),
    session_id: z.string(),
    status: z.enum(webSocketTransportStatuses),
    details: z.string().optional().nullable(),
    timestamp: z.number().optional().nullable(),
  })
  .passthrough();

export const webSocketResponseErrorSchema = z
  .object({
    type: z.literal("response.error"),
    thread_id: z.string(),
    session_id: z.string(),
    code: z.string().optional().nullable(),
    message: z.string().optional().nullable(),
    details: z.string().optional().nullable(),
    timestamp: z.number().optional().nullable(),
  })
  .passthrough();

/**
 * Live thread-title update. The backend generates a title asynchronously (once
 * a conversation has real substance, or after a topic shift) and ships it as a
 * dedicated `response.thread_title` event so the sidebar can update immediately
 * — rather than waiting for the next `response.done` or a thread-list refetch.
 *
 */
export const webSocketResponseThreadTitleSchema = z
  .object({
    type: z.literal("response.thread_title"),
    thread_id: z.string(),
    session_id: z.string(),
    title: z.string(),
  })
  .passthrough();

export type WebSocketResponseThreadTitle = z.infer<
  typeof webSocketResponseThreadTitleSchema
>;

/**
 * Safe-parse a raw inbound envelope as `response.thread_title`. Returns null on
 * any mismatch so chat UIs can ignore malformed/empty title events.
 */
export function parseResponseThreadTitle(
  raw: unknown,
): WebSocketResponseThreadTitle | null {
  const result = webSocketResponseThreadTitleSchema.safeParse(raw);
  return result.success ? result.data : null;
}

/**
 * `response.intake` — the intake variables' state after the post-turn
 * extraction merged values (docs/intake.md in the backend). Full state, not a
 * delta: the client replaces what it shows. Arrives asynchronously, typically
 * after `response.done`, and only on modules whose intake is active. `type`
 * stays a plain string so a new backend variable type never drops the whole
 * event; the UI localises the ones it knows and shows the rest verbatim.
 */
export const webSocketResponseIntakeSchema = z
  .object({
    type: z.literal("response.intake"),
    thread_id: z.string(),
    session_id: z.string(),
    vars: z.array(
      z
        .object({
          name: z.string(),
          type: z.string(),
          required: z.boolean(),
          value: z.string().optional(),
          collected: z.boolean(),
          // in-process validation (docs/intake.md, "Validation") — every field
          // optional so an older backend keeps parsing
          verified: z.boolean().optional(),
          raw: z.string().optional(),
          source: z.string().optional(),
          validation: z
            .object({
              status: z.string(),
              reason: z.string().optional(),
              house_number: z.string().optional(),
              checked_at: z.string().optional(),
              candidates: z
                .array(
                  z
                    .object({
                      display: z.string(),
                      postal_codes: z.array(z.string()).optional(),
                      locality: z.string().optional(),
                      score: z.number(),
                    })
                    .passthrough(),
                )
                .optional(),
            })
            .passthrough()
            .optional(),
        })
        .passthrough(),
    ),
    all_required_collected: z.boolean(),
    needs_confirmation: z.array(z.string()).optional(),
    version: z.number().optional(),
  })
  .passthrough();

export type WebSocketResponseIntake = z.infer<
  typeof webSocketResponseIntakeSchema
>;

/**
 * Safe-parse a raw inbound envelope as `response.intake`. Returns null on any
 * mismatch so chat UIs can ignore malformed events.
 */
export function parseResponseIntake(
  raw: unknown,
): WebSocketResponseIntake | null {
  const result = webSocketResponseIntakeSchema.safeParse(raw);
  return result.success ? result.data : null;
}

export type WebSocketResponseStart = z.infer<
  typeof webSocketResponseStartSchema
>;
export type WebSocketResponseDelta = z.infer<
  typeof webSocketResponseDeltaSchema
>;
export type WebSocketResponseDone = z.infer<typeof webSocketResponseDoneSchema>;
export type WebSocketResponseStatus = z.infer<
  typeof webSocketResponseStatusSchema
>;
export type WebSocketResponseError = z.infer<
  typeof webSocketResponseErrorSchema
>;
export type WebSocketDoneStatistic = z.infer<
  typeof webSocketDoneStatisticSchema
>;

const INBOUND_TYPE_SHORTHAND: Record<string, string> = {
  delta: "response.delta",
  start: "response.start",
  done: "response.done",
  status: "response.status",
  error: "response.error",
};

/**
 * Coerce `type` to string, map shorthand stream names, default missing `content`
 * on deltas. Does not validate the rest of the payload — that is intentional.
 */
export function preprocessInboundStreamEnvelope(value: unknown): unknown {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return value;
  }
  const o = value as Record<string, unknown>;
  const rawType = o.type;

  const t =
    typeof rawType === "string"
      ? rawType
      : typeof rawType === "number"
        ? String(rawType)
        : "";

  const canonical = INBOUND_TYPE_SHORTHAND[t] ?? t;
  const next: Record<string, unknown> = { ...o, type: canonical };

  if (canonical === "response.delta" && next.content === undefined) {
    next.content = [];
  }

  return next;
}

/**
 * Inbound stream messages: only require a string `type` after preprocess.
 * All other fields are passthrough — validate `content[]` with
 * {@link parseDeltaContentItems} and `status` with {@link isWebSocketTransportStatus}.
 */
export const webSocketInboundEnvelopeSchema = z.preprocess(
  preprocessInboundStreamEnvelope,
  z
    .object({
      type: z.string(),
      thread_id: z.string().optional(),
      session_id: z.string().optional(),
    })
    .passthrough(),
);

export type WebSocketInboundEvent = z.infer<
  typeof webSocketInboundEnvelopeSchema
>;

/** @alias {@link WebSocketInboundEvent} */
export type WebSocketEvent = WebSocketInboundEvent;

/** @alias {@link webSocketInboundEnvelopeSchema} */
export const webSocketEventSchema = webSocketInboundEnvelopeSchema;

export function isWebSocketTransportStatus(
  value: unknown,
): value is WebSocketTransportStatus {
  return (
    typeof value === "string" &&
    (webSocketTransportStatuses as readonly string[]).includes(value)
  );
}

/** Safe-parse each `response.delta` content item; drop malformed entries. */
export function parseDeltaContentItems(raw: unknown): WebSocketDeltaContent[] {
  if (!Array.isArray(raw)) return [];
  const out: WebSocketDeltaContent[] = [];

  for (const item of raw) {
    const r = webSocketDeltaContentSchema.safeParse(item);
    if (r.success) out.push(r.data);
  }
  return out;
}

export const turnStartContentItemSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("text"),
    text: z.string(),
  }),
  z.object({
    type: z.literal("file"),
    upload_id: z.string(),
    mime: z.string(),
    name: z.string(),
    sample_rate: z.number().optional(),
  }),
]);

export const webSocketTurnStartSchema = z.object({
  type: z.literal("turn.start"),
  stream: z.literal(true),
  thread_id: z.string(),
  content: z.array(turnStartContentItemSchema),
  tools_enabled: z.array(z.string()).optional(),
  verbosity: z.string().optional(),
  reasoning_effort: z.string().optional(),
});

export const webSocketTurnEndSchema = z.object({
  type: z.literal("turn.end"),
  thread_id: z.string(),
});

export const webSocketTurnCancelSchema = z.object({
  type: z.literal("turn.cancel"),
  thread_id: z.string(),
});

/**
 * Per-call voice toggle overrides sent on voice.start (the call screen's
 * quick settings). Absent fields mean "use the assistant's setting"; the
 * backend resolves override → assistant setting → default, with speed_mode
 * forcing thinking/RAG off. Additive: an older backend ignores the key.
 *
 * PII detection is deliberately NOT overridable from the client: redaction is
 * an org-level compliance control (organization settings; phone calls have a
 * separate per-assistant toggle in the phone channel settings). The backend
 * ignores a stale `pii_detection` key.
 */
export const webSocketVoiceOverridesSchema = z.object({
  speed_mode: z.boolean().optional(),
  thinking: z.boolean().optional(),
  rag: z.boolean().optional(),
});

export const webSocketVoiceStartSchema = z.object({
  type: z.literal("voice.start"),
  thread_id: z.string(),
  room_name: z.string(),
  overrides: webSocketVoiceOverridesSchema.optional(),
});

export const webSocketVoiceEndSchema = z.object({
  type: z.literal("voice.end"),
  thread_id: z.string(),
});

/**
 * Browser-side voice call events for the backend's flight recorder (backend
 * docs/stream_flow.md, `client.telemetry`). `sent_at_ms` and each `at_ms`
 * are this browser's clock; the server cancels the offset. Fire-and-forget:
 * the server never replies and ignores it when the call is not recorded.
 */
export const clientTelemetryEventNames = [
  "mic_permission",
  "mic_device",
  "mic_track_published",
  "remote_audio_start",
  "remote_audio_stop",
  "audio_context",
  "network",
  "visibility",
  "connection_quality",
  "reconnecting",
  "reconnected",
  "error",
] as const;

export const webSocketClientTelemetrySchema = z.object({
  type: z.literal("client.telemetry"),
  thread_id: z.string(),
  sent_at_ms: z.number(),
  events: z.array(
    z.object({
      event: z.enum(clientTelemetryEventNames),
      at_ms: z.number(),
      attrs: z
        .record(z.string(), z.union([z.string(), z.number(), z.boolean()]))
        .optional(),
    }),
  ),
});

export const webSocketOutboundMessageSchema = z.discriminatedUnion("type", [
  webSocketTurnStartSchema,
  webSocketTurnEndSchema,
  webSocketTurnCancelSchema,
  webSocketVoiceStartSchema,
  webSocketVoiceEndSchema,
  webSocketClientTelemetrySchema,
]);

export type WebSocketClientTelemetry = z.infer<
  typeof webSocketClientTelemetrySchema
>;
export type ClientTelemetryEventName =
  (typeof clientTelemetryEventNames)[number];

export type WebSocketTurnStart = z.infer<typeof webSocketTurnStartSchema>;
export type WebSocketTurnEnd = z.infer<typeof webSocketTurnEndSchema>;
export type WebSocketTurnCancel = z.infer<typeof webSocketTurnCancelSchema>;
export type WebSocketVoiceOverrides = z.infer<
  typeof webSocketVoiceOverridesSchema
>;
export type WebSocketVoiceStart = z.infer<typeof webSocketVoiceStartSchema>;
export type WebSocketVoiceEnd = z.infer<typeof webSocketVoiceEndSchema>;
export type WebSocketOutboundMessage = z.infer<
  typeof webSocketOutboundMessageSchema
>;

/** WebSocket API `OPEN` / react-use-websocket `ReadyState.OPEN`. */
export const WEB_SOCKET_OPEN = 1 as const;

/**
 * Sentinel WebSocket subprotocol offered alongside the JWT, i.e.
 * `new WebSocket(url, [WS_AUTH_SUBPROTOCOL, accessToken])`. Keeping the token
 * in the Sec-WebSocket-Protocol header (rather than the URL query string)
 * stops it leaking via access logs and browser history. The backend echoes
 * only this sentinel back — must stay in sync with httpx.WSAuthSubprotocol.
 */
export const WS_AUTH_SUBPROTOCOL = "edge-ws-v1";

export function isWebSocketOpen(readyState: number): boolean {
  return readyState === WEB_SOCKET_OPEN;
}
